// ─── Design Tokens ──────────────────────────────────────────────────────────
import type { CSSProperties, ReactNode } from "react";

export const T = {
    bg: "var(--background)",
    surface: "var(--card)",
    card: "var(--card)",
    card2: "var(--secondary)",
    border: "var(--border)",
    borderSoft: "var(--border)",
    primary: "var(--primary)",
    green: "#4ade80",
    red: "#f87171",
    amber: "#fbbf24",
    purple: "#a78bfa",
    text: "var(--foreground)",
    mid: "var(--muted-foreground)",
    dim: "var(--muted-foreground)",
    mono: "var(--font-mono)",
    serif: "var(--font-serif)",
    display: "var(--font-display)",
    h: "1.2rem",
    sub: "1rem",
    body: "0.9rem",
};

// ─── Global CSS Component ──────────────────────────────────────────────────
export const GlobalStyles = () => (
    <style dangerouslySetInnerHTML={{
        __html: `
    
    @keyframes blink   { 0%,100%{opacity:1;transform:scale(1)} 50%{opacity:.25;transform:scale(.6)} }
    @keyframes fadeUp  { from{opacity:0;transform:translateY(12px)} to{opacity:1;transform:translateY(0)} }
    @keyframes fadeIn  { from{opacity:0} to{opacity:1} }
    @keyframes zoomIn  { from{opacity:0;transform:scale(.96)} to{opacity:1;transform:scale(1)} }

    .blink      { animation: blink 2s infinite; }
    .fade-up    { animation: fadeUp .4s ease both; }
    .fade-in    { animation: fadeIn .3s ease both; }
    .zoom-in    { animation: zoomIn .25s cubic-bezier(.34,1.4,.64,1) both; }

    .s1{animation-delay:.04s} .s2{animation-delay:.08s} .s3{animation-delay:.12s}
    .s4{animation-delay:.16s} .s5{animation-delay:.20s} .s6{animation-delay:.24s}
    .s7{animation-delay:.28s} .s8{animation-delay:.32s}

    .tab-btn {
      display:flex; align-items:center; gap:7px;
      padding:8px 16px; border-radius:6px;
      border:none; background:transparent; cursor:pointer;
      font-family:var(--font-sans); font-size:.78rem;
      letter-spacing:.02em; text-transform:none;
      color: var(--muted-foreground); transition:all .18s; white-space:nowrap;
    }
    .tab-btn:hover { color: var(--foreground); background: var(--secondary); }
    .tab-btn.active { color: var(--primary); background: var(--accent); }

    .card-hover { transition:border-color .2s,box-shadow .2s; }
    .card-hover:hover { border-color: var(--primary); opacity: 0.9; }

    .row-hover:hover { background: var(--secondary); }

    .btn-primary {
      font-family:var(--font-sans); font-size:.78rem; letter-spacing:.02em;
      padding:8px 18px; border-radius:100px; border:none;
      background: var(--primary); color: var(--primary-foreground); cursor:pointer;
      transition:opacity .2s; display:inline-flex; align-items:center; gap:6px;
    }
    .btn-primary:hover { opacity:.82; }

    .btn-ghost {
      font-family:var(--font-sans); font-size:.75rem; letter-spacing:.02em;
      padding:7px 15px; border-radius:100px;
      border:1px solid var(--border); background:transparent;
      color: var(--muted-foreground); cursor:pointer; transition:all .2s;
      display:inline-flex; align-items:center; gap:6px;
    }
    .btn-ghost:hover { border-color: var(--primary); color: var(--foreground); }

    .lh-input {
      width:100%; background: var(--input);
      border:1px solid var(--border); border-radius:4px;
      padding:9px 12px; font-family:var(--font-sans);
      font-size:.85rem; color: var(--foreground); outline:none;
      transition:border-color .2s;
    }
    .lh-input::placeholder { color: var(--muted-foreground); }
    .lh-input:focus { border-color: var(--primary); }

    .mono-label {
      font-family:var(--font-sans); font-size:.65rem;
      letter-spacing:.05em; text-transform:uppercase;
      color: var(--muted-foreground); margin-bottom:4px;
    }

    .toggle-track {
      width:36px; height:18px; border-radius:100px;
      position:relative; cursor:pointer; border:none;
      transition:background .2s;
      flex-shrink:0;
    }
    .toggle-thumb {
      position:absolute; top:2px; width:14px; height:14px;
      border-radius:50%; background: var(--foreground); transition:left .2s;
    }

    .channel-card {
      padding:22px; background: var(--card);
      border:1px solid var(--border); border-radius:6px;
      transition:border-color .2s;
    }
    .channel-card:hover { border-color: var(--primary); }
  ` }} />
);

// ─── Micro Components ───────────────────────────────────────────────────────
interface CardProps {
    children: ReactNode;
    style?: CSSProperties;
    hover?: boolean;
    glow?: boolean;
    className?: string;
}

interface CardHeaderProps {
    title: ReactNode;
    sub?: ReactNode;
    subDateTime?: string;
    right?: ReactNode;
}

interface TagProps {
    c?: string;
    children: ReactNode;
}

interface PillProps {
    active?: boolean;
    color?: string;
    children: ReactNode;
    style?: CSSProperties;
    className?: string;
}

interface MonoProps {
    children: ReactNode;
    size?: string;
    color?: string;
    style?: CSSProperties;
    className?: string;
    title?: string;
}

interface StatDotProps {
    active?: boolean;
}

interface BarProps {
    pct: number;
    color?: string;
    h?: number;
}

interface ToggleProps {
    enabled: boolean;
    onChange: (enabled: boolean) => void;
}

export const Card = ({ children, style, hover, glow, className }: CardProps) => (
    <div className={`${hover ? "card-hover" : ""} ${className || ""}`} style={{
        background: T.card, borderRadius: 8,
        border: `1px solid ${glow ? "rgba(0,184,219,.3)" : T.border}`,
        boxShadow: glow ? "0 0 20px rgba(0,184,219,.07)" : "none",
        overflow: "hidden", ...style,
    }}>{children}</div>
);

export const CardHeader = ({ title, sub, subDateTime, right }: CardHeaderProps) => (
    <div style={{ padding: "14px 18px", borderBottom: `1px solid ${T.borderSoft}`, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <div>
            <div style={{ fontFamily: T.serif, fontSize: "1.2rem", fontWeight: 700, color: T.text }}>{title}</div>
            {sub && subDateTime ? (
              <time dateTime={subDateTime} style={{ fontFamily: T.mono, fontSize: ".65rem", color: T.dim, marginTop: 2, display: "block" }}>{sub}</time>
            ) : sub ? (
              <div style={{ fontFamily: T.mono, fontSize: ".65rem", color: T.dim, marginTop: 2 }}>{sub}</div>
            ) : null}
        </div>
        {right}
    </div>
);

export const Tag = ({ c = T.primary, children }: TagProps) => (
    <span style={{
        fontFamily: T.mono, fontSize: ".65rem", letterSpacing: ".02em", textTransform: "uppercase",
        padding: "2px 7px", borderRadius: 3,
        background: `${c}18`, border: `1px solid ${c}28`, color: c,
        display: "inline-flex", alignItems: "center",
    }}>{children}</span>
);

export const Pill = ({ active, color, children, style, className }: PillProps) => {
    const col = color || (active ? T.primary : T.mid);
    return (
        <span className={className} style={{
            fontFamily: T.mono, fontSize: ".7rem", letterSpacing: ".02em", textTransform: "uppercase",
            padding: "3px 9px", borderRadius: 100,
            background: active ? `${col}16` : "rgba(255,255,255,0.05)",
            border: `1px solid ${active ? col : T.borderSoft}`, color: col,
            display: "inline-flex", alignItems: "center", gap: 4, ...style,
        }}>{children}</span>
    );
};

export const Mono = ({ children, size = ".75rem", color = T.dim, style, className, title }: MonoProps) => (
    <span className={className} title={title} style={{ fontFamily: T.mono, fontSize: size, color, ...style }}>{children}</span>
);

export const StatDot = ({ active }: StatDotProps) => (
    <span className={active ? "blink" : ""} style={{
        width: 6, height: 6, borderRadius: "50%", display: "inline-block", flexShrink: 0,
        background: active ? T.green : "rgba(255,255,255,.15)",
        boxShadow: active ? `0 0 5px ${T.green}90` : "none",
    }} />
);

export const Bar = ({ pct, color = T.primary, h = 3 }: BarProps) => (
    <div style={{ height: h, background: "rgba(255,255,255,.06)", borderRadius: h }}>
        <div style={{ height: "100%", width: `${pct}%`, background: color, borderRadius: h, transition: "width 1s ease" }} />
    </div>
);

export const Toggle = ({ enabled, onChange }: ToggleProps) => (
    <button className="toggle-track" style={{ background: enabled ? T.primary : "#162130" }} onClick={() => onChange(!enabled)}>
        <div className="toggle-thumb" style={{ left: enabled ? "20px" : "2px" }} />
    </button>
);

